/**
 * @documental
 * @export Field
 * @name
 * TextArea.Field
 */

import { useEffect, useMemo, FC } from "react";
import { useController, useFormContext } from "react-hook-form";
import { FieldBaseProps } from "../FieldBaseProps";
import { SharedProps } from "./SharedProps";
import { Input } from "./Input";
import { required as requiredRule, length as lengthRule, DisabledRequiredRule, DisabledLengthRule } from "../validation/rules";
import { useInputDisabled } from "../InputGroup/useInputDisabled";
import { useInputReadonly } from "../InputGroup/useInputReadonly";
import { SubmitGroup } from "../SubmitGroup";
import { ValidityGroup } from "../ValidityGroup";

/**
 * The `Field` component properties.
 */

export interface FieldProps extends FieldBaseProps<string | null>, SharedProps {
  /**
   * The minimum number of characters allowed in the text area.
   */
  readonly minLength?: number;
}

/**
 * A React Hook Form compatible TextArea Field component. For more information, see the documentation for
 * the <a href="https://react-hook-form.com/api">React Hook Form API</a>.
 */
export const Field: FC<FieldProps> = (props) => {
  const { name, required = false, minLength, maxLength, defaultValue = null, validator, ...restProps } = props;

  const disabled = useInputDisabled(props);
  const readOnly = useInputReadonly(props);

  const rules = useMemo(
    () => ({
      ...(disabled || readOnly ? DisabledRequiredRule : requiredRule(required)),
      ...(disabled || readOnly ? DisabledLengthRule : lengthRule(minLength, maxLength)),
      validate: disabled || readOnly ? undefined : validator,
    }),
    [disabled, readOnly, required, minLength, maxLength, validator],
  );

  const { resetField } = useFormContext();

  useEffect(() => {
    if (disabled) {
      resetField(name);
    }
  }, [disabled]);

  const { field, fieldState, formState } = useController({
    name,
    defaultValue,
    rules,
  });

  const { ref, value, onChange, onBlur } = field;
  const { invalid, error } = fieldState;
  const { isSubmitting } = formState;

  return (
    <SubmitGroup submitting={isSubmitting}>
      <ValidityGroup invalid={invalid} error={error?.message}>
        <Input
          {...restProps}
          ref={ref}
          value={value}
          required={required}
          maxLength={maxLength}
          defaultValue={defaultValue}
          onChange={onChange}
          onFocusExit={onBlur}
        />
      </ValidityGroup>
    </SubmitGroup>
  );
};
